/**
 * Yelp Error Mapping + Secret Sanitization (Story 23.5 AC #4, #6)
 *
 * Yelp Fusion publishes structured error codes in the response body's
 * `error.code` field but the HTTP status is the canonical signal per
 * docs.developer.yelp.com/docs/fusion-errors verified 2026-05-18. The parser
 * is HTTP-status-first with a per-status disambiguation pass on `error.code`
 * for 429 (distinguishing QPS burst from daily quota exhaustion).
 *
 * **Round 3 Red Team Attack #1 + #2 — secret-leak defense.** The
 * `sanitizeYelpErrorMessage` helper redacts:
 *  - Bearer API Keys (`Authorization: Bearer <YELP_API_KEY>`)
 *  - Plaintext key occurrences anywhere in the message
 *  - URL-encoded variants
 *
 * The redacted plaintext form is in scope for the entire `fetchMetrics`
 * lifetime — defensive against accidental log-leak.
 */
import type { AdapterError, AdapterErrorCode } from "../adapter";

import { sanitizeErrorMessage } from "../safe-fetch";

/**
 * Yelp Fusion error envelope shape. Per
 * docs.developer.yelp.com/docs/fusion-errors verified 2026-05-18, errors
 * arrive as `{ error: { code, description? } }` with codes like
 * `VALIDATION_ERROR`, `TOKEN_INVALID`, `ACCESS_LIMIT_REACHED`,
 * `BUSINESS_NOT_FOUND`, `TOO_MANY_REQUESTS_PER_SECOND`,
 * `DAILY_QUOTA_EXCEEDED`, `INTERNAL_ERROR`.
 */
export interface YelpApiError {
  readonly code?: string;
  readonly description?: string;
}

/**
 * Custom error class for Yelp API call failures.
 * Mirrors Ahrefs/Semrush/Moz/CallRail/Trustpilot's `*ApiCallError` pattern
 * so callers can `instanceof` discriminate.
 */
export class YelpApiCallError extends Error {
  readonly code: AdapterErrorCode;
  readonly providerStatus?: number;
  readonly providerCode?: string;
  readonly retryable: boolean;
  readonly retryAfterMs?: number;

  constructor(
    message: string,
    code: AdapterErrorCode,
    providerStatus?: number,
    retryable: boolean = false,
    retryAfterMs?: number,
    providerCode?: string,
  ) {
    super(message);
    this.name = "YelpApiCallError";
    this.code = code;
    this.providerStatus = providerStatus;
    this.providerCode = providerCode;
    this.retryable = retryable;
    this.retryAfterMs = retryAfterMs;
  }
}

/**
 * Yelp does NOT publish a Retry-After header per
 * docs.developer.yelp.com/docs/fusion-errors verified 2026-05-18.
 *
 * - For QPS bursts (`TOO_MANY_REQUESTS_PER_SECOND`): 1-second backoff
 *   matches the documented ~5 QPS soft ceiling.
 * - For daily quota (`DAILY_QUOTA_EXCEEDED`): retryable=false until UTC
 *   midnight. The queue caller is expected to surface a user-facing
 *   "resets at midnight UTC" message and stop further attempts.
 */
export const YELP_QPS_BURST_BACKOFF_MS = 1_000;
export const YELP_DAILY_QUOTA_CODE = "DAILY_QUOTA_EXCEEDED" as const;
export const YELP_QPS_BURST_CODE = "TOO_MANY_REQUESTS_PER_SECOND" as const;

/**
 * Parse an `AdapterError` from a raw Yelp Response per AC #6:
 *
 *  - 400 → `validation_error` (surface `error.description` if present)
 *  - 401 → `token_expired` (cron triggers `recoveryRefresh` PR-1 stub)
 *  - 403 → `permission_denied` (subscription gate when body mentions
 *    "subscription" / "plan" / "tier" — surface upgrade message)
 *  - 404 → `account_not_found` (the businessId no longer resolves; caller
 *    cross-checks against `metadata.businessId`. The original story spec
 *    said `endpoint_not_found`, but `account_not_found` matches the
 *    AC #21 / AC #22 surface contract — pinned 2026-05-18 per the
 *    Round 1 Acceptance Auditor finding.)
 *  - 429 with code `TOO_MANY_REQUESTS_PER_SECOND` → `rate_limited`
 *    retryable=true with 1s backoff
 *  - 429 with code `DAILY_QUOTA_EXCEEDED` (or no code) → `rate_limited`
 *    retryable=false
 *  - 5xx → `provider_unavailable` retryable=true
 */
export function parseYelpApiError(
  status: number,
  body?: string,
): {
  code: AdapterErrorCode;
  retryable: boolean;
  retryAfterMs?: number;
  message?: string;
  providerCode?: string;
} {
  const parsed = extractYelpErrorBody(body);
  const providerCode = parsed?.code;
  const description = parsed?.description;

  if (status === 400) {
    return {
      code: "invalid_request",
      retryable: false,
      message: description,
      providerCode,
    };
  }
  if (status === 401) {
    return { code: "token_expired", retryable: false, providerCode };
  }
  if (status === 403) {
    const planMention =
      description && /(subscription|plan|tier|upgrade)/i.test(description)
        ? ` (upgrade your Yelp Fusion subscription to access this endpoint)`
        : "";
    return {
      code: "permission_denied",
      retryable: false,
      message: description ? `${description}${planMention}` : undefined,
      providerCode,
    };
  }
  if (status === 404) {
    return {
      code: "account_not_found",
      retryable: false,
      message: description,
      providerCode,
    };
  }
  if (status === 429) {
    // Normalize providerCode for resilience against case drift in upstream
    // CDN responses (Round 1 Blind Hunter — Yelp docs occasionally show
    // mixed-case codes).
    const normalizedCode = providerCode?.toUpperCase();
    if (normalizedCode === YELP_QPS_BURST_CODE) {
      return {
        code: "rate_limited",
        retryable: true,
        retryAfterMs: YELP_QPS_BURST_BACKOFF_MS,
        message: description,
        providerCode,
      };
    }
    if (normalizedCode === YELP_DAILY_QUOTA_CODE) {
      return {
        code: "rate_limited",
        retryable: false,
        message:
          description ??
          "Yelp daily call budget exhausted — resets at midnight UTC.",
        providerCode,
      };
    }
    // Round 1 review fix (Edge Case Hunter Finding 6): when `error.code` is
    // ABSENT (CDN-layer 429 with empty body), prefer retryable=true with the
    // QPS-burst backoff. Cheaper to retry once than to break a sync until
    // midnight on what's likely a transient burst.
    return {
      code: "rate_limited",
      retryable: true,
      retryAfterMs: YELP_QPS_BURST_BACKOFF_MS,
      message: description,
      providerCode,
    };
  }
  // Round 1 review fix (Blind Hunter): map HTTP 408 (Request Timeout) to
  // provider_unavailable retryable=true; without this branch it fell into
  // `invalid_response` which is non-retryable.
  if (status === 408) {
    return {
      code: "provider_unavailable",
      retryable: true,
      message: description,
      providerCode,
    };
  }
  if (status >= 500) {
    return {
      code: "provider_unavailable",
      retryable: true,
      message: description,
      providerCode,
    };
  }
  return {
    code: "invalid_response",
    retryable: false,
    message: description,
    providerCode,
  };
}

function extractYelpErrorBody(body: string | undefined): YelpApiError | undefined {
  if (!body) return undefined;
  try {
    const parsed = JSON.parse(body);
    if (parsed && typeof parsed === "object") {
      const root = parsed as Record<string, unknown>;
      const err = root.error;
      if (err && typeof err === "object") {
        const obj = err as Record<string, unknown>;
        const out: YelpApiError = {
          code: typeof obj.code === "string" ? obj.code : undefined,
          description:
            typeof obj.description === "string" ? obj.description : undefined,
        };
        return out;
      }
    }
  } catch {
    // Not JSON — return raw body truncated.
    return { description: body.length > 200 ? `${body.slice(0, 200)}…` : body };
  }
  return undefined;
}

/**
 * Sanitize a Yelp error message — redact the API Key value AND URL-encoded
 * variants AND any `Authorization: Bearer ...` literal that survived the
 * generic redactor.
 *
 * Per Round 3 Red Team Attack #1 + #2: the decrypted plaintext is in scope
 * for the entire `fetchMetrics` lifetime. This helper is the last line of
 * defense before an error message reaches `console.log`, a Sentry
 * breadcrumb, or a queue error payload.
 */
export function sanitizeYelpErrorMessage(
  message: string,
  decryptedApiKey?: string,
): string {
  // Generic first-pass (long base64 + Bearer + ?token=).
  let sanitized = sanitizeErrorMessage(message);

  if (decryptedApiKey && decryptedApiKey.length > 0) {
    sanitized = replaceAllWithVariants(
      sanitized,
      decryptedApiKey,
      "[redacted-yelp-api-key]",
    );
  }

  // Belt-and-braces: any remaining literal `Authorization: Bearer <token>`.
  sanitized = sanitized.replace(
    /Authorization:\s*Bearer\s+\S+/gi,
    "Authorization: Bearer [redacted-yelp-api-key]",
  );
  return sanitized;
}

function replaceAllWithVariants(
  haystack: string,
  needle: string,
  replacement: string,
): string {
  if (needle.length === 0) return haystack;
  const escapedNeedle = escapeRegExp(needle);
  let out = haystack.replace(new RegExp(escapedNeedle, "g"), replacement);
  try {
    const encoded = encodeURIComponent(needle);
    if (encoded !== needle && encoded.length > 0) {
      out = out.replace(new RegExp(escapeRegExp(encoded), "g"), replacement);
    }
  } catch {
    // Unencodable — skip.
  }
  return out;
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * Build an `AdapterError` from a raw Yelp response (used by the adapter's
 * `fetchMetrics` + `validateConnection` paths).
 */
export function buildYelpAdapterError(
  status: number,
  body: string | undefined,
  context: { decryptedApiKey?: string },
): AdapterError {
  const parsed = parseYelpApiError(status, body);
  const baseMessage = parsed.message ?? `HTTP ${status} from api.yelp.com`;
  return {
    code: parsed.code,
    message: sanitizeYelpErrorMessage(baseMessage, context.decryptedApiKey),
    retryable: parsed.retryable,
    retryAfterMs: parsed.retryAfterMs,
    providerStatus: status,
    providerMessage: parsed.message,
  };
}
